'use client';

import { CardHeader } from '@components';
import Icon from '@components/typography/AppIcon';
import React, { ReactNode, useState } from 'react';
import { Cell, Pie, PieChart } from 'recharts';

type Props = {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  onClick?: () => void;

  data: Record<string, number>;
  config: DonutConfigItem[];
  total: number;
};

type DonutConfigItem = {
  key: string;
  label: string;
  color: string;
};

export const PeerHealthCard = ({
  title,
  subtitle,
  icon,
  onClick,
  data,
  config,
  total = 0,
}: Props) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const chartData = config
    .map((item) => ({
      name: item.label,
      value: data[item.key] ?? 0,
      color: item.color,
    }))
    .filter((item) => item.value > 0);

  return (
    <div className="rounded-xl px-4 py-3 flex flex-col border dark:border-gray-700">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <CardHeader title={title} subtitle={subtitle} icon={icon} />

        {onClick && (
          <button
            onClick={onClick}
            className="flex items-center justify-center p-2 rounded-md 
              text-zinc-500 hover:text-zinc-900 
              dark:text-zinc-400 dark:hover:text-white
              hover:bg-zinc-200 dark:hover:bg-zinc-800
              transition"
          >
            <Icon name="ArrowUpRight" size="md" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex items-center justify-center gap-6 h-44">
        {/* DONUT */}
        <div className="relative w-[160px] h-[160px] flex-shrink-0">
          <PieChart width={160} height={160}>
            <Pie
              data={chartData}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={58}
              outerRadius={78}
              paddingAngle={2}
              stroke="transparent"
              cornerRadius={4}
              activeIndex={activeIndex ?? -1}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={index}
                  fill={entry.color}
                  opacity={
                    activeIndex === null || activeIndex === index ? 1 : 0.4
                  }
                />
              ))}
            </Pie>
          </PieChart>

          {/* Center */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
            <span className="text-2xl font-semibold leading-none">{total}</span>
            <span className="text-[10px] uppercase tracking-wide text-zinc-500">
              peers
            </span>
          </div>
        </div>

        {/* LEGEND */}
        <div className="flex flex-col justify-center gap-2 min-w-[140px]">
          {chartData.map((item, i) => {
            const percentage = total
              ? Math.round((item.value / total) * 100)
              : 0;

            return (
              <div
                key={i}
                className="flex items-center justify-between gap-4 px-2 py-1.5 rounded-md 
                  hover:bg-zinc-100 dark:hover:bg-zinc-800 transition cursor-pointer"
                onMouseEnter={() => setActiveIndex(i)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                <div className="flex items-center gap-2">
                  <span className="relative flex size-2.5">
                    <span
                      className={`absolute inline-flex h-full w-full rounded-full ${
                        item.name === 'Pending'
                          ? 'animate-ping opacity-30'
                          : 'opacity-75'
                      }`}
                      style={{ backgroundColor: item.color }}
                    />
                    <span
                      className="relative inline-flex size-2.5 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                  </span>

                  <span className="text-zinc-600 dark:text-zinc-300 whitespace-nowrap">
                    {item.name}
                    <span className="ml-2 text-[10px] text-zinc-400">
                      {percentage}%
                    </span>
                  </span>
                </div>

                <span className="font-semibold tabular-nums">{item.value}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
